import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  Ref,
} from 'react';
import { cloneElement, isValidElement, useId, useState } from 'react';
import { AlertTriangle, Inbox, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { Sparkline } from './Sparkline.js';
import { t } from '../../lib/strings.js';
import { Button } from './Button.js';

export { Button } from './Button.js';

/* ------------------------------------------------------------------ Card */

export function Panel({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={cn(
        'rimati-panel rounded-card border border-line bg-surface shadow-panel',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function PanelHeader({
  title,
  action,
  hint,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}): ReactNode {
  return (
    /*
      RIM-176: `flex-wrap` e `basis-48` no titulo.

      Sem eles, a acao nao encolhia e espremia a coluna de texto ate uma palavra
      por linha — em 768px o titulo "Simulador de cliente" descia empilhado e
      passava por cima do botao. Abaixo de 192px de espaco o titulo fica com a
      linha inteira e a acao desce para a proxima.
    */
    <div className="rimati-panel-header flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b border-line px-5 py-3.5">
      <div className="min-w-0 flex-1 basis-48">
        <h2 className="text-body font-semibold tracking-[0.01em] text-ink">{title}</h2>
        {hint ? <p className="mt-0.5 text-xs leading-snug text-ink-muted">{hint}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * Grupo reutilizável de indicadores operacionais.
 *
 * O domínio fornece os nomes, valores e explicações; este componente mantém
 * a mesma hierarquia, espaçamento e comportamento responsivo em qualquer
 * negócio (clínica, loja, oficina ou outro segmento).
 */
export function MetricGroupPanel({
  title,
  hint,
  metrics,
}: {
  title: string;
  hint: string;
  metrics: Array<{ label: string; value: ReactNode; hint: string }>;
}): ReactNode {
  if (!metrics.length) return null;

  return (
    <Panel>
      <PanelHeader title={title} hint={hint} />
      <dl className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="min-h-32 rounded-control border border-line bg-surface-muted p-4"
          >
            <dt className="text-xs font-medium text-ink-muted">{metric.label}</dt>
            <dd className="mt-2 text-2xl font-semibold tabular-nums text-brand">{metric.value}</dd>
            <dd className="mt-1 text-xs leading-relaxed text-ink-muted">{metric.hint}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/* ------------------------------------------------------------- PageHeader */

export function PageHeader({
  title,
  subtitle,
  actions,
  icon,
  className,
  level = 1,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  icon?: ReactNode;
  className?: string;
  level?: 1 | 2;
}): ReactNode {
  const Heading = level === 1 ? 'h1' : 'h2';
  return (
    <header
      className={cn('rimati-page-header flex flex-wrap items-end justify-between gap-4', className)}
    >
      <div className="flex min-w-0 flex-1 basis-64 items-start gap-3">
        {icon && (
          <span className="tool-icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <Heading className="text-[22px] leading-7 font-semibold tracking-[-0.01em] text-ink">
            {title}
          </Heading>
          {subtitle ? <p className="mt-1 text-sm text-ink-muted">{subtitle}</p> : null}
        </div>
      </div>
      {actions ? (
        <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}

/* ------------------------------------------------------------------ Badge */

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-sunken text-ink-soft border-line',
  brand: 'bg-brand-soft text-brand-text border-brand-border',
  success: 'bg-success-soft text-success-text border-success/20',
  warning: 'bg-warning-soft text-warning-text border-warning/20',
  danger: 'bg-danger-soft text-danger-text border-danger/20',
  info: 'bg-info-soft text-info border-info/20',
};

export function Badge({
  tone = 'neutral',
  icon,
  children,
  className,
  title,
}: {
  tone?: BadgeTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Texto completo quando o rotulo precisa ser curto para caber. */
  title?: string;
}): ReactNode {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
        'text-caption leading-4 font-medium whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

/* ----------------------------------------------------------------- Avatar */

export function Avatar({
  name,
  src,
  size = 'md',
  tone = 'neutral',
}: {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
  tone?: 'neutral' | 'brand';
}): ReactNode {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const sizes = {
    sm: 'size-7 text-caption',
    md: 'size-9 text-xs',
    lg: 'size-11 text-sm',
  };
  const label = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold select-none',
        tone === 'brand'
          ? 'bg-brand-soft text-brand-text'
          : 'bg-surface-sunken text-ink-soft ring-1 ring-line',
        sizes[size],
      )}
    >
      {src && src !== failedSource ? (
        <img
          src={src}
          alt=""
          className="size-full rounded-full object-cover"
          onError={() => setFailedSource(src)}
        />
      ) : (
        label || '?'
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ Input */

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor: string;
  children: ReactNode;
}): ReactNode {
  /*
    R101 - a mensagem do erro e LIGADA ao campo, e nao so desenhada embaixo.

    O paragrafo do erro ja tinha `id`, e nada o referenciava: quem usa leitor de
    tela ouvia "invalido" e nunca o motivo. O aviso existia na tela e nao existia
    para quem depende de ouvir a tela — que e a pessoa para quem "erro aponta
    campo" importa mais.
    
    Ligar aqui, e nao em cada formulario, e o que faz todo uso futuro nascer
    correto: um `aria-describedby` que depende de alguem lembrar e um que uma
    hora vai faltar.
  */
  const hintId = `${htmlFor}-hint`;
  const errorId = `${htmlFor}-error`;
  const descrito = isValidElement(children)
    ? (() => {
        const child = children as React.ReactElement<{
          'aria-describedby'?: string;
          'aria-invalid'?: boolean | 'true' | 'false';
        }>;
        const ids = [child.props['aria-describedby'], hint ? hintId : '', error ? errorId : '']
          .flatMap((value) => value?.split(/\s+/) ?? [])
          .filter(Boolean);
        return cloneElement(child, {
          ...(ids.length ? { 'aria-describedby': [...new Set(ids)].join(' ') } : {}),
          ...(error ? { 'aria-invalid': true } : {}),
        });
      })()
    : children;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {descrito}
      {/* Erro fica junto do campo e explica como corrigir (Apendice H.10). */}
      {hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * R099 - o CHIP, num lugar so.
 *
 * O produto tinha seis controles com `aria-pressed` em QUATRO tratamentos
 * visuais. Dois eram identicos, dois divergiam por UM token cada, e um era
 * deliberadamente diferente. As divergencias de um token nao foram escolhas:
 * ninguem decide que o filtro de Resultados tem canto menos redondo que o de
 * Oportunidades — isso acontece quando cada tela escreve o seu.
 *
 * DUAS VARIANTES, porque sao dois trabalhos:
 *
 * - `filtro`: recorta uma lista que continua na tela. Discreto, porque o
 *   conteudo e que importa.
 * - `escolha`: seleciona uma opcao dentro de uma conversa, onde o chip E o
 *   assunto. Solido, porque a resposta precisa ficar obvia depois de dada.
 *
 * O controle de hoje/semana da Agenda NAO entra aqui: ele vive dentro de um
 * `role="group"` com borda, e um grupo segmentado e outro componente — tem
 * moldura propria, altura maior e os itens se tocam. Force-lo a virar chip
 * mudaria a forma de um controle que esta certo.
 */
export function Chip({
  ativo,
  variante = 'filtro',
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  ativo: boolean;
  variante?: 'filtro' | 'escolha';
}): ReactNode {
  const aparencia =
    variante === 'escolha'
      ? cn(
          'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
          ativo
            ? 'border-brand bg-brand text-white'
            : 'border-line bg-surface text-ink-soft hover:bg-surface-sunken hover:text-ink',
        )
      : cn(
          'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
          ativo
            ? 'bg-brand-soft text-brand-text'
            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
        );

  return (
    <button type="button" aria-pressed={ativo} className={cn(aparencia, className)} {...rest}>
      {children}
    </button>
  );
}

export function Label({
  className,
  children,
  ...rest
}: LabelHTMLAttributes<HTMLLabelElement>): ReactNode {
  return (
    <label className={cn('text-xs font-medium text-ink-soft', className)} {...rest}>
      {children}
    </label>
  );
}

/*
  A borda usa `control-border` (3:1 contra o fundo, WCAG 1.4.11), nao `line`,
  que e o tom discreto de divisorias e continua assim. O foco e um anel de 2 px
  na cor da marca (nao a versao a 15% de opacidade, que ficava abaixo de 3:1);
  a borda tambem vira a cor da marca. O `focus-visible` global de index.css e
  mantido para os demais elementos.
*/
const CONTROL_CLASS =
  'rimati-control w-full rounded-control border border-control-border bg-surface px-3 text-sm text-ink ' +
  'transition-[border-color,box-shadow] duration-[160ms] ' +
  'hover:border-ink-muted focus:border-brand focus:ring-2 focus:ring-brand focus:outline-none ' +
  'disabled:bg-surface-sunken disabled:text-ink-muted';

export function Input({
  className,
  invalid,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }): ReactNode {
  return (
    <input
      className={cn(
        CONTROL_CLASS,
        'h-10',
        invalid && 'border-danger focus:border-danger',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  );
}

export function Textarea({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }): ReactNode {
  return <textarea className={cn(CONTROL_CLASS, 'py-2.5 leading-relaxed', className)} {...rest} />;
}

/**
 * Select nativo com a mesma altura, borda e foco de `Input`. Dentro de `Field`
 * recebe `id`, `aria-describedby` e `aria-invalid` automaticamente; fora dele,
 * passe `invalid` e `aria-describedby` manualmente.
 */
export function Select({
  className,
  invalid,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }): ReactNode {
  return (
    <select
      className={cn(
        CONTROL_CLASS,
        'h-10',
        invalid && 'border-danger focus:border-danger',
        className,
      )}
      aria-invalid={invalid || undefined}
      {...rest}
    >
      {children}
    </select>
  );
}

export function useFieldId(prefix: string): string {
  const id = useId();
  return `${prefix}-${id}`;
}

/* -------------------------------------------------------------- Skeletons */

export function Skeleton({ className }: { className?: string }): ReactNode {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse-soft rounded-control bg-surface-sunken', className)}
    />
  );
}

export function SkeletonList({ rows = 4 }: { rows?: number }): ReactNode {
  return (
    <div className="flex flex-col gap-2 p-4" aria-label={t.common.loading} role="status">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-14" />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ States */

export function EmptyState({
  title,
  hint,
  icon,
  action,
  compact = false,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}): ReactNode {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'gap-2 px-5 py-8' : 'gap-3 px-6 py-14',
      )}
    >
      <span className="flex size-10 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
        {icon ?? <Inbox className="size-[18px]" strokeWidth={1.75} />}
      </span>
      <p className="text-sm font-medium text-ink">{title}</p>
      {hint ? <p className="max-w-[48ch] text-sm leading-relaxed text-ink-muted">{hint}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({
  title = t.common.errorTitle,
  hint = t.common.errorHint,
  onRetry,
}: {
  title?: string;
  hint?: string;
  onRetry?: () => void;
}): ReactNode {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <span className="flex size-10 items-center justify-center rounded-full bg-danger-soft text-danger-text">
        <AlertTriangle className="size-[18px]" strokeWidth={1.75} />
      </span>
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="max-w-[42ch] text-xs leading-relaxed text-ink-muted">{hint}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- MetricCard */

export type TrendDirection = 'up' | 'down' | 'flat';

const TREND_STYLE: Record<TrendDirection, { className: string; icon: ReactNode }> = {
  up: { className: 'text-success-text', icon: <TrendingUp className="size-3.5" strokeWidth={2} /> },
  down: { className: 'text-rose-700', icon: <TrendingDown className="size-3.5" strokeWidth={2} /> },
  flat: { className: 'text-ink-muted', icon: <Minus className="size-3.5" strokeWidth={2} /> },
};

export function MetricCard({
  label,
  value,
  hint,
  note,
  trend,
  series,
  loading = false,
}: {
  label: string;
  value: string;
  hint?: string;
  /**
   * RESULTADOS WOW (secao 37) - a definicao da metrica, quando ela pode gerar
   * duvida.
   *
   * Fica abaixo do `hint` e em cinza mais claro: "38% do valor convertido"
   * responde quanto, e a nota responde DE QUE. Numa tela de dinheiro, um numero
   * cuja definicao ninguem sabe vale menos do que numero nenhum.
   *
   * Opcional de proposito — os cartoes do Inicio nao passam nada aqui e ficam
   * exatamente como estavam.
   */
  note?: string;
  /** Comparacao com o periodo anterior. Sem base de comparacao, so o texto. */
  trend?: TrendDirection;
  /**
   * RIM-178 - o formato do periodo, quando o servidor manda historico.
   * Ausente e o caso normal no comeco de uso: sem linha, o cartao fica igual
   * ao que era.
   */
  series?: number[];
  loading?: boolean;
}): ReactNode {
  const style = trend ? TREND_STYLE[trend] : null;

  return (
    <Panel className="px-5 py-4">
      <p className="text-xs font-medium text-ink-muted">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-20" />
      ) : (
        <p className="mt-1 text-[26px] leading-8 font-semibold tabular-nums tracking-[-0.02em] text-ink">
          {value}
        </p>
      )}
      {note && !loading ? (
        <p className="mt-1 text-caption leading-snug text-ink-faint">{note}</p>
      ) : null}
      {hint && !loading ? (
        <p
          className={cn(
            'mt-1 flex items-start gap-1 text-xs sm:items-center',
            style?.className ?? 'text-ink-muted',
          )}
        >
          {style?.icon}
          {/*
            RIM-194 - quebra no celular, corta no desktop.

            Em 360px com dois cartoes por linha, `truncate` deixava "+471% que
            n…" — que nao informa nada e ainda ocupa a linha inteira. Ali sobra
            altura, entao o texto quebra. No desktop, onde a linha e larga e o
            alinhamento entre cartoes importa, o corte com reticencias continua.
          */}
          <span className="leading-snug sm:truncate" title={hint}>
            {hint}
          </span>
        </p>
      ) : null}
      {series && !loading ? <Sparkline values={series} /> : null}
    </Panel>
  );
}
